import React from "react";
import { useAtom } from "jotai";
import {
  blockIdsAtom,
  defaultSpringStiffnessAtom,
  defaultSpringDampingAtom,
  defaultSpringMassAtom,
  userLocationsAtom,
  consoleOnAtom,
  useOnlines,
  streamsAtom,
} from "../jotai/atoms";
import { styled } from "../stitches.config";
import {
  useFeatureFlags,
  useSpaceInfo,
  useUserInfos,
} from "../roomservice/maps";
import { usePositions } from "../roomservice/presences";
import { defaultFeatureFlags, featureFlagLabels, FeatureFlags } from "../types";
import { Button } from "../components/Button";
import { DEFAULT_CURSOR_SIZE } from "../consts";

function Console() {
  const [consoleOn, setConsoleOn] = useAtom(consoleOnAtom);
  let [featureFlags, featureFlagsClient] = useFeatureFlags();
  const [spaceInfo, spaceInfoClient] = useSpaceInfo();
  const cursorSize = spaceInfo?.cursorSize || DEFAULT_CURSOR_SIZE;
  const onlines = useOnlines();
  const [streams] = useAtom(streamsAtom);
  const [blockIds] = useAtom(blockIdsAtom);
  const positions = usePositions();
  const [userLocations] = useAtom(userLocationsAtom);
  const userInfos = useUserInfos();
  const [stiffness, setStiffness] = useAtom(defaultSpringStiffnessAtom);
  const [damping, setDamping] = useAtom(defaultSpringDampingAtom);
  const [mass, setMass] = useAtom(defaultSpringMassAtom);

  if (!consoleOn) return <></>;

  return (
    <>
      <Panel>
        <div>
          <Slider
            label="Cursor Video Size"
            value={cursorSize}
            step={4}
            min={32}
            max={256}
            setter={(value: number) =>
              spaceInfoClient?.set("cursorSize", value)
            }
          />
          {featureFlags &&
            Object.keys(defaultFeatureFlags).map((flag) => {
              if (!featureFlags) return <></>;
              return (
                <OptionLabel>
                  <input
                    key={flag}
                    type="checkbox"
                    name={flag}
                    checked={featureFlags[flag as keyof FeatureFlags]}
                    disabled={flag === "spatialAudio"}
                    onChange={() => {
                      if (!featureFlags) return;
                      featureFlagsClient?.set(
                        flag as keyof FeatureFlags,
                        !featureFlags[flag as keyof FeatureFlags]
                      );
                    }}
                  />{" "}
                  {featureFlagLabels[flag as keyof FeatureFlags]}
                </OptionLabel>
              );
            })}
        </div>

        <details>
          <summary>Spring Tweaks</summary>
          <ul>
            <Slider
              label="Default spring Stiffness"
              value={stiffness}
              setter={(value: number) => setStiffness(value)}
            />
            <Slider
              label="Default spring Damping"
              value={damping}
              setter={(value: number) => setDamping(value)}
            />
            <Slider
              label="Default spring Mass"
              value={mass}
              setter={(value: number) => setMass(value)}
            />
          </ul>
        </details>

        <details>
          <summary>Debug Details</summary>
          <ul>
            <Message label="Positions" json={positions} />
            <Message label="Locations" json={userLocations} />
            <Message label="Users" json={userInfos} />
            {blockIds ? (
              <Message
                label={`Blocks ${blockIds.keys.length}`}
                json={blockIds.keys}
              />
            ) : null}
            <Message label="Online" json={onlines} />
            <Message label="Streams" json={prettyPrintStreams(streams)} />
          </ul>
        </details>
        <Button
          style={{ position: "absolute", right: "0", top: "0" }}
          icon="Close"
          color="secondary"
          size=""
          onClick={() => {
            setConsoleOn(false);
          }}
        ></Button>
      </Panel>
    </>
  );
}

function prettyPrintStreams(streams: Record<string, AgoraRTC.Stream>) {
  let out: any = {};
  let keys = Object.keys(streams);

  keys.forEach((key: string) => {
    let stream = streams[key] as any;
    out[key] = {
      video: !stream?.userMuteVideo,
      audio: !stream?.userMuteAudio,
    };
  });
  return out;
}

export default Console;
const OptionLabel = styled("label", {
  display: "block",
});
const Panel = styled("aside", {
  width: "50vw",
  maxHeight: "50vh",
  overflow: "hidden",
  padding: ".5rem",
  background: "$menuBeige",
  backgroundBlur: "",
  borderRadius: ".25rem",
  cursor: "default",
  position: "fixed",
  left: "$2",
  bottom: "$2",
  listStyle: "none",
});

function Message({ label, json }: { label: string; json: object | undefined }) {
  return (
    <MessageListItem>
      <details>
        <summary>{label}</summary>
        {json ? <pre>{JSON.stringify(json, null, 2)}</pre> : null}
      </details>
    </MessageListItem>
  );
}
const MessageListItem = styled("li", {
  marginBottom: ".5rem",
});

export function Slider({
  label,
  value,
  setter,
  min = 0,
  max = 500,
  step = 1,
}: {
  label: string;
  value: number;
  setter: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <MessageListItem>
      <label>
        {`${label}: ${value}`}
        <input
          style={{
            width: "100%",
          }}
          type="range"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setter(Number(e.target.value))
          }
        />
      </label>
    </MessageListItem>
  );
}
